import axios, { AxiosInstance } from 'axios'
import { IPublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'
import { apiTokenRequest } from './authConfig'
import type { CorreccionEnviada } from '@/utils/packingList'
import type { CorreccionHsEnviada } from '@/utils/hsCode'
import type { FacturacionReferencia, VentasSituacion } from '@/types'
import type {
  CargaSalida,
  CargaSalidaLinea,
  ArticuloCatalogo,
  ComentarioCarga,
  ComentarioCreateRequest,
  CuadroMuelles,
  SlotMuelle,
  CamionMuelle,
  AsignacionCarga,
  CamionCreateRequest,
  CamionUpdateRequest,
  SlotCreateRequest,
  SlotUpdateRequest,
  ApiResponse,
  AlmacenVenta,
  GrupoCliente,
  LecturaArchivo,
  PedidoArchivoRequest,
  PedidoArchivoResult,
  SolicitudPedidoArchivo,
  RutaCabecera,
  ProduccionCabecera,
  ConsumoRow,
  PlanificacionSemanalRow,
  EntradaCompraRow,
  InventarioRow,
  EntradaPrevistaRow,
  ReportRawMaterialsRow,
  ReportFgRow,
  ReportFgToRmRow,
  ReportConsumoProduccionRow,
  ProduccionDiariaRow,
  ConsumoOpRow,
  ConsumoAgregadoRow,
  ConsumoProduccionRow,
  PedidoCompraRow,
  PedidoCompraLinea,
  PedidoVentaRow,
  PedidoVentaLinea,
  PedidoVentaLineaReciente,
  AlbaranLinea,
  CargaLinea,
  CargaCreateRequest,
  CargaCreateResult,
  SolicitudCarga,
  AdjuntoDoc,
  AccionLinea,
  BackOrder,
  EstadoManualBody,
  EstadoManualResultado,
  AvisoClienteResultado,
  FacturaCarga,
  HsCodeDatos,
  AlbaranRecogidaBody,
  AlbaranRecogidaDatos,
  PackingListDatos,
  PackingListCabeceraEditable,
  DocumentoGenerado,
  DocumentoOrigen,
  DashboardResumen,
  ProduccionRiesgoItem,
  ProduccionSemanalPoint,
  PlanFeed,
  PlanExportBody,
  PlanEntradaDetalle,
  PlanScenario,
  PlanScenarioDetail,
  SecuenciaProduccionRow,
  ComponenteSecuenciaRow,
  OverlayBody,
  OverlayResult,
  SecuenciaOverlayRow,
  ArticuloOpcion,
  ClienteOpcion,
  DireccionNuevaForm,
  DimensionArticulo,
  DireccionEnvio,
  PedidoVentaCabeceraForm,
  PedidoVentaCreateResult,
  PedidoVentaDetalle,
  PedidoVentaLineaForm,
} from '@/types'
import type {
  SistemaBOM,
  VersionBOM,
  BOMCompleto,
  CapturaBOM,
  CapturaCreateRequest,
  CapturaUpdateRequest,
  ImagenResponse,
  ImagenUploadResponse,
  GenerarFichaResponse,
  VarianteCaracteristicas,
} from '@/types/bomCaptura'

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

// ─── Axios instance ───────────────────────────────────────────────────────────

let _msalInstance: IPublicClientApplication | null = null

export function initApiAuth(msal: IPublicClientApplication) {
  _msalInstance = msal
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

// Attach Bearer token on every request
apiClient.interceptors.request.use(async (config) => {
  if (!_msalInstance) return config

  const accounts = _msalInstance.getAllAccounts()
  if (accounts.length === 0) return config

  try {
    const result = await _msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account: accounts[0],
    })
    config.headers.Authorization = `Bearer ${result.accessToken}`
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      try {
        const result = await _msalInstance.acquireTokenPopup({
          ...apiTokenRequest,
          account: accounts[0],
        })
        config.headers.Authorization = `Bearer ${result.accessToken}`
      } catch (popupErr) {
        if (import.meta.env.DEV) console.warn('[API] acquireTokenPopup failed:', popupErr)
      }
    } else {
      if (import.meta.env.DEV) console.warn('[API] acquireTokenSilent failed:', err)
    }
  }
  return config
})

// ─── Consumos / Planificación semanal ────────────────────────────────────────

export const consumosApi = {
  getRutas: () =>
    apiClient.get<ApiResponse<RutaCabecera[]>>('/v1/consumos/rutas').then((r) => r.data),

  getProducciones: (weekOffset = 0) =>
    apiClient
      .get<ApiResponse<ProduccionCabecera[]>>('/v1/consumos/producciones', { params: { weekOffset } })
      .then((r) => r.data),

  getPlanificacion: (prodid: string) =>
    apiClient
      .get<ApiResponse<ConsumoRow[]>>(`/v1/consumos/planificacion/${prodid}`)
      .then((r) => r.data),

  getPlanificacionSemanal: (weekOffset = 0) =>
    apiClient
      .get<ApiResponse<PlanificacionSemanalRow[]>>('/v1/consumos/planificacion-semanal', { params: { weekOffset } })
      .then((r) => r.data),

  getProduccionesMes: (monthOffset = 0) =>
    apiClient
      .get<ApiResponse<ProduccionCabecera[]>>('/v1/consumos/producciones-mes', { params: { monthOffset } })
      .then((r) => r.data),

  getPlanificacionMensual: (monthOffset = 0) =>
    apiClient
      .get<ApiResponse<PlanificacionSemanalRow[]>>('/v1/consumos/planificacion-mensual', { params: { monthOffset } })
      .then((r) => r.data),

  getProduccionesAnio: (yearOffset = 0) =>
    apiClient
      .get<ApiResponse<ProduccionCabecera[]>>('/v1/consumos/producciones-anio', { params: { yearOffset } })
      .then((r) => r.data),

  getPlanificacionAnual: (yearOffset = 0) =>
    apiClient
      .get<ApiResponse<PlanificacionSemanalRow[]>>('/v1/consumos/planificacion-anual', { params: { yearOffset } })
      .then((r) => r.data),

  getEntradasCompra: (params: { itemid: string; inventcolorid?: string; inventstyleid?: string; dataareaid?: string }) =>
    apiClient
      .get<ApiResponse<EntradaCompraRow[]>>('/v1/inventario/entradas-compra', { params })
      .then((r) => r.data),

  getEntradasVariante: (params: { itemid: string; inventcolorid?: string; inventstyleid?: string; dataareaid?: string }) =>
    apiClient
      .get<ApiResponse<EntradaCompraRow[]>>('/v1/inventario/entradas-variante', { params })
      .then((r) => r.data),
}

// ─── Inventario Disponible ────────────────────────────────────────────────────

export interface InventarioParams {
  almacen?: string
  linea?: string
  familia?: string
  articulo?: string
  solo_con_stock?: boolean
}

export const inventarioApi = {
  getInventario: (params?: InventarioParams) =>
    apiClient
      .get<ApiResponse<InventarioRow[]>>('/v1/inventario', { params })
      .then((r) => r.data),
  /** Calendario de llegadas de compra, para el semáforo de material. */
  getEntradasPrevistas: () =>
    apiClient
      .get<ApiResponse<EntradaPrevistaRow[]>>('/v1/inventario/entradas-previstas')
      .then((r) => r.data),
}

export const authApi = {
  /** Las empresas del usuario y lo que puede ver.
   *
   *  `sinProduccion` llega RESUELTO desde el servidor, no la lista de roles: la
   *  regla de quién fabrica y quién sólo compra y vende es suya, y repartirla
   *  por el navegador la pondría en dos sitios que se separan sin avisar. */
  getMe: () =>
    apiClient
      .get<{
        empresas: string[]
        isEmpresaAll: boolean
        sinProduccion: boolean
        /** Modulos que abre, o `null` si NO tiene restriccion de area.
         *  `null` y `[]` no son lo mismo: el primero es «lo ve todo» —el caso
         *  de todos los usuarios de siempre— y el segundo seria «nada». */
        modulos: string[] | null
      }>('/v1/auth/me')
      .then((r) => r.data),
}

/**
 * Reports de cliente (Sterling / ARMAF).
 *
 * Los tres devuelven la vista completa, ya ordenada por (ordenBloque,
 * ordenDetalle), sin parámetros. El filtrado vive en el cliente porque la unidad
 * de filtrado es el bloque completo (cabecera + sublíneas), y son datasets de
 * cientos de filas.
 */
export const reportsApi = {
  getRawMaterials: () =>
    apiClient
      .get<ApiResponse<ReportRawMaterialsRow[]>>('/v1/reports/raw-materials')
      .then((r) => r.data),
  getFg: () =>
    apiClient.get<ApiResponse<ReportFgRow[]>>('/v1/reports/fg').then((r) => r.data),
  getFgToRm: () =>
    apiClient.get<ApiResponse<ReportFgToRmRow[]>>('/v1/reports/fg-to-rm').then((r) => r.data),
  getConsumoProduccion: () =>
    apiClient
      .get<ApiResponse<ReportConsumoProduccionRow[]>>('/v1/reports/consumo-produccion')
      .then((r) => r.data),
}

export const produccionDiariaApi = {
  getProduccionDiaria: (params?: { desde?: string; hasta?: string }) =>
    apiClient
      .get<ApiResponse<ProduccionDiariaRow[]>>('/v1/produccion-diaria', { params })
      .then((r) => r.data),
  getConsumosOp: (numOp: string) =>
    apiClient
      .get<ApiResponse<ConsumoOpRow[]>>('/v1/produccion-diaria/consumos', { params: { num_op: numOp } })
      .then((r) => r.data),
  getAdjuntos: (numOp: string) =>
    apiClient
      .get<ApiResponse<AdjuntoDoc[]>>(`/v1/produccion-diaria/${encodeURIComponent(numOp)}/adjuntos`)
      .then((r) => r.data),
  getConsumosAgregado: (params?: { desde?: string; hasta?: string; empresa?: string }) =>
    apiClient
      .get<ApiResponse<ConsumoAgregadoRow[]>>('/v1/produccion-diaria/consumos-agregado', { params })
      .then((r) => r.data),
  getConsumosPorComponente: (componente: string, params?: { desde?: string; hasta?: string; empresa?: string }) =>
    apiClient
      .get<ApiResponse<ConsumoProduccionRow[]>>(`/v1/produccion-diaria/consumos-componente/${encodeURIComponent(componente)}`, { params })
      .then((r) => r.data),
  getConsumosExport: (params?: { desde?: string; hasta?: string; empresa?: string }) =>
    apiClient
      .get<ApiResponse<ConsumoProduccionRow[]>>('/v1/produccion-diaria/consumos-export', { params })
      .then((r) => r.data),
}

export const pedidosCompraApi = {
  getPedidos: (params?: { desde?: string; hasta?: string }) =>
    apiClient
      .get<ApiResponse<PedidoCompraRow[]>>('/v1/pedidos-compra', { params })
      .then((r) => r.data),

  getLineas: (purchid: string) =>
    apiClient
      .get<ApiResponse<PedidoCompraLinea[]>>(`/v1/pedidos-compra/${encodeURIComponent(purchid)}/lineas`)
      .then((r) => r.data),

  getLineasBulk: (pedidos: string[]) =>
    apiClient
      .post<ApiResponse<PedidoCompraLinea[]>>('/v1/pedidos-compra/lineas-bulk', { pedidos })
      .then((r) => r.data),

  getAdjuntos: (purchid: string) =>
    apiClient
      .get<ApiResponse<AdjuntoDoc[]>>(`/v1/pedidos-compra/${encodeURIComponent(purchid)}/adjuntos`)
      .then((r) => r.data),

  /** Adjuntos de una factura (reusa el flujo PA con num_pedido=recid_factura, tipo=factura). */
  getAdjuntosFactura: (recid: string) =>
    apiClient
      .get<ApiResponse<AdjuntoDoc[]>>(`/v1/pedidos-compra/facturas/${encodeURIComponent(recid)}/adjuntos`)
      .then((r) => r.data),
}

export const pedidosVentasApi = {
  getPedidos: (params?: { desde?: string; hasta?: string }) =>
    apiClient
      .get<ApiResponse<PedidoVentaRow[]>>('/v1/pedidos-ventas', { params })
      .then((r) => r.data),

  /** Overlay: pedidos creados en las últimas OVERLAY_HORAS, leídos en vivo de
   *  F&O vía Dataverse. Tapa el retraso de ~15 min de Fabric. Mismo schema que
   *  getPedidos, con los importes a cero y pendienteSync=true. */
  getRecientes: () =>
    apiClient
      .get<ApiResponse<PedidoVentaRow[]>>('/v1/pedidos-ventas/recientes')
      .then((r) => r.data),

  getLineas: (salesid: string) =>
    apiClient
      .get<ApiResponse<PedidoVentaLinea[]>>(`/v1/pedidos-ventas/${encodeURIComponent(salesid)}/lineas`)
      .then((r) => r.data),

  /** Preparación de cargas (picking) de un pedido, agregada por carga+artículo. */
  getCargas: (salesid: string) =>
    apiClient
      .get<ApiResponse<CargaLinea[]>>(
        `/v1/pedidos-ventas/${encodeURIComponent(salesid)}/cargas`,
      )
      .then((r) => r.data),

  /** Cargas de VARIOS pedidos en una llamada (exportación a Excel).
   *  La tabla de origen ya viene precalculada: pedirlas de una en una era gastar
   *  doscientas consultas para leer lo mismo. */
  getCargasBulk: (pedidos: string[]) =>
    apiClient
      .post<ApiResponse<CargaLinea[]>>('/v1/pedidos-ventas/cargas-bulk', { pedidos })
      .then((r) => r.data),

  /** Albaranes (packing slips) de un pedido, por línea de venta y factura. */
  getAlbaranes: (salesid: string) =>
    apiClient
      .get<ApiResponse<AlbaranLinea[]>>(
        `/v1/pedidos-ventas/${encodeURIComponent(salesid)}/albaranes`,
      )
      .then((r) => r.data),

  /** Albaranes de VARIOS pedidos en una llamada (exportación a Excel), por el
   *  mismo motivo que `getCargasBulk`. */
  getAlbaranesBulk: (pedidos: string[]) =>
    apiClient
      .post<ApiResponse<AlbaranLinea[]>>('/v1/pedidos-ventas/albaranes-bulk', { pedidos })
      .then((r) => r.data),

  /** Cargas pedidas que todavía no existen: el robot no ha terminado o Fabric
   *  aún no las enseña. Aparte de getCargas a propósito — aquélla se cachea y
   *  ésta cambia en cuanto el usuario envía. */
  getSolicitudesCarga: (salesid: string) =>
    apiClient
      .get<ApiResponse<SolicitudCarga[]>>(
        `/v1/pedidos-ventas/${encodeURIComponent(salesid)}/cargas/solicitudes`,
      )
      .then((r) => r.data),

  /** Solicita crear una carga. Devuelve 202: la carga no existe todavía. */
  crearCarga: (salesid: string, body: CargaCreateRequest) =>
    apiClient
      .post<ApiResponse<CargaCreateResult>>(
        `/v1/pedidos-ventas/${encodeURIComponent(salesid)}/cargas`,
        body,
      )
      .then((r) => r.data),

  /** Líneas de un pedido que aún no está en Fabric (overlay de Dataverse). */
  getLineasRecientes: (salesid: string) =>
    apiClient
      .get<ApiResponse<PedidoVentaLineaReciente[]>>(
        `/v1/pedidos-ventas/${encodeURIComponent(salesid)}/lineas-recientes`,
      )
      .then((r) => r.data),

  getLineasBulk: (pedidos: string[]) =>
    apiClient
      .post<ApiResponse<PedidoVentaLinea[]>>('/v1/pedidos-ventas/lineas-bulk', { pedidos })
      .then((r) => r.data),

  getAdjuntos: (salesid: string) =>
    apiClient
      .get<ApiResponse<AdjuntoDoc[]>>(`/v1/pedidos-ventas/${encodeURIComponent(salesid)}/adjuntos`)
      .then((r) => r.data),

  /** Adjuntos de una factura (reusa el flujo PA con tipo=factura, id=recid_factura). */
  getAdjuntosFactura: (recid: string) =>
    apiClient
      .get<ApiResponse<AdjuntoDoc[]>>(`/v1/pedidos-ventas/facturas/${encodeURIComponent(recid)}/adjuntos`)
      .then((r) => r.data),
}

// ─── Generación documental (DocGen) ───────────────────────────────────────────
// El PDF llega en base64 dentro de ApiResponse. La function key de la Azure
// Function es un secreto de SERVIDOR: el navegador habla con nuestro backend y
// nunca con el servicio de generación.

export const documentosApi = {
  /** Qué saldría en el packing list de esta carga, y qué falta. No genera nada:
   *  alimenta el cajón para que el usuario revise antes. */
  getPackingListDatos: (empresa: string, carga: string, origen?: DocumentoOrigen) =>
    apiClient
      .get<ApiResponse<PackingListDatos>>('/v1/documentos/packing-list', {
        // `origen` fuerza la fuente de palets y pesos; omitido, la elige el
        // criterio automático del backend (picking >= solicitada).
        params: { empresa, carga, ...(origen ? { origen } : {}) },
      })
      .then((r) => r.data),

  /** Genera el packing list. No se mandan líneas: las relee el backend. El
   *  cliente no decide qué lleva un documento que sale con el nombre de la
   *  empresa. */
  generarPackingList: (body: {
    empresa: string
    carga: string
    idioma: 'es' | 'en'
    origen?: DocumentoOrigen
    cabecera: PackingListCabeceraEditable
    /** Correcciones manuales de línea. Solo lo que cambió; el backend relee las
     *  líneas de Fabric y aplica esto encima, auditando el antes→después. */
    lineas?: CorreccionEnviada[]
  }) =>
    apiClient
      .post<ApiResponse<DocumentoGenerado>>('/v1/documentos/packing-list', body)
      .then((r) => r.data),

  /** Qué saldría en el listado por código HS. Grano distinto al del packing: una
   *  fila por LOTE, no por (matrícula × artículo). */
  getHsCodeDatos: (empresa: string, carga: string, origen?: DocumentoOrigen) =>
    apiClient
      .get<ApiResponse<HsCodeDatos>>('/v1/documentos/hs-code', {
        params: { empresa, carga, ...(origen ? { origen } : {}) },
      })
      .then((r) => r.data),

  generarHsCode: (body: {
    empresa: string
    carga: string
    idioma: 'es' | 'en'
    origen?: DocumentoOrigen
    cabecera: PackingListCabeceraEditable
    lineas?: CorreccionHsEnviada[]
  }) =>
    apiClient
      .post<ApiResponse<DocumentoGenerado>>('/v1/documentos/hs-code', body)
      .then((r) => r.data),

  /** Genera el multimodal de mercancías peligrosas.
   *
   *  No tiene GET: se construye sobre las MISMAS líneas del packing, así que los
   *  datos ya están y sólo cambia cómo se agrupan. Por eso admite también las
   *  mismas correcciones. */
  generarMultimodal: (body: {
    empresa: string
    carga: string
    idioma: 'es' | 'en'
    origen?: DocumentoOrigen
    cabecera: PackingListCabeceraEditable
    lineas?: CorreccionEnviada[]
  }) =>
    apiClient
      .post<ApiResponse<DocumentoGenerado>>('/v1/documentos/multimodal', body)
      .then((r) => r.data),

  /** Qué precarga el albarán de recogida. Tarda ~3,6 s en frío: se pide al abrir
   *  el panel, no al pulsar «Generar». */
  getAlbaranRecogidaDatos: (empresa: string, carga: string) =>
    apiClient
      .get<ApiResponse<AlbaranRecogidaDatos>>('/v1/documentos/albaran-recogida', {
        params: { empresa, carga },
      })
      .then((r) => r.data),

  /** Genera el albarán. El cuerpo va en snake_case y SIN totales. */
  generarAlbaranRecogida: (body: AlbaranRecogidaBody) =>
    apiClient
      .post<ApiResponse<DocumentoGenerado>>('/v1/documentos/albaran-recogida', body)
      .then((r) => r.data),
}

// ─── Órdenes de servicio (crear pedidos de venta) ─────────────────────────────
// Artículos y dimensiones color/estilo desde Fabric; clientes, direcciones y
// pedidos (crear/listar/editar) desde Dataverse (entidades virtuales D365 F&O,
// escritura síncrona: lo creado se ve al instante).

export const ordenesVentaApi = {
  /** Artículos buscables desde Fabric, filtrados por empresa y ARMAF (rol). */
  buscarArticulos: (q: string) =>
    apiClient
      .get<ApiResponse<ArticuloOpcion[]>>('/v1/ordenes-venta/articulos', { params: { q } })
      .then((r) => r.data.data),

  /** Combinaciones color/estilo disponibles de un artículo (Fabric). */
  dimensiones: (itemid: string, empresa?: string) =>
    apiClient
      .get<ApiResponse<DimensionArticulo[]>>(
        `/v1/ordenes-venta/articulos/${encodeURIComponent(itemid)}/dimensiones`,
        { params: { empresa } },
      )
      .then((r) => r.data.data),

  /** Clientes desde Dataverse (rol externo: solo su cuenta principal). */
  buscarClientes: (q: string) =>
    apiClient
      .get<ApiResponse<ClienteOpcion[]>>('/v1/ordenes-venta/clientes', { params: { q } })
      .then((r) => r.data.data),

  /** Cliente al que amarra el rol (cliente externo), o null si elige libremente. */
  clienteFijo: () =>
    apiClient
      .get<ApiResponse<ClienteOpcion | null>>('/v1/ordenes-venta/clientes/fijo')
      .then((r) => r.data.data),

  /** Direcciones postales reales del cliente (Dataverse). */
  direcciones: (cuenta: string, empresa?: string) =>
    apiClient
      .get<ApiResponse<DireccionEnvio[]>>(
        `/v1/ordenes-venta/clientes/${encodeURIComponent(cuenta)}/direcciones`,
        { params: { empresa } },
      )
      .then((r) => r.data.data),

  /** Añade una dirección de envío a un cliente existente. */
  crearDireccion: (cuenta: string, form: DireccionNuevaForm) =>
    apiClient
      .post<ApiResponse<DireccionEnvio>>(
        `/v1/ordenes-venta/clientes/${encodeURIComponent(cuenta)}/direcciones`,
        form,
        { timeout: 60_000 },
      )
      .then((r) => r.data.data),

  // El listado vive en pedidosVentasApi (Fabric + overlay de Dataverse). Aquí
  // sólo queda lo que alimenta el formulario de alta y edición.

  /** Detalle de un pedido (cabecera + líneas con lineId para edición). */
  getDetalle: (numero: string, empresa?: string) =>
    apiClient
      .get<ApiResponse<PedidoVentaDetalle>>(
        `/v1/ordenes-venta/${encodeURIComponent(numero)}`,
        { params: { empresa } },
      )
      .then((r) => ({ ...r.data.data, cargas: r.data.data.cargas ?? [] })),

  /** Crear pedido (cabecera + líneas). Puede devolver advertencias parciales. */
  crear: (cab: PedidoVentaCabeceraForm, lineas: PedidoVentaLineaForm[]) =>
    apiClient
      .post<ApiResponse<PedidoVentaCreateResult>>('/v1/ordenes-venta/', {
        cabecera: cab,
        lineas,
      }, { timeout: 120_000 })
      .then((r) => r.data.data),

  /** Solicitar la cancelación de un pedido abierto (aviso a administración
   *  vía Power Automate; la cancelación real es un proceso interno de Dynamics). */
  solicitarCancelacion: (numero: string, empresa?: string, motivo?: string) =>
    apiClient
      .post<ApiResponse<string>>(
        `/v1/ordenes-venta/${encodeURIComponent(numero)}/solicitar-cancelacion`,
        { motivo: motivo ?? '', empresa: empresa ?? '' },
        { timeout: 60_000 },
      )
      .then((r) => r.data.data),

  /** Editar pedido (solo estado "Pedido abierto"). */
  actualizar: (numero: string, cab: PedidoVentaCabeceraForm, lineas: PedidoVentaLineaForm[]) =>
    apiClient
      .put<ApiResponse<PedidoVentaCreateResult>>(
        `/v1/ordenes-venta/${encodeURIComponent(numero)}`,
        { cabecera: cab, lineas },
        { timeout: 120_000 },
      )
      .then((r) => r.data.data),
}

// ─── Plan de producción por formato/semana ────────────────────────────────────

export const planProduccionApi = {
  /** Feed completo para la simulación en cliente (BOM, stock, entradas, consumos, salidas). */
  getFeed: (params?: { empresa?: string; anio?: number; linea?: string; formato?: string }) =>
    apiClient
      .get<ApiResponse<PlanFeed>>('/v1/plan-produccion/feed', { params, timeout: 120_000 })
      .then((r) => r.data),

  /** Vuelca la rejilla calculada a XLSX. Devuelve el blob para descargar. */
  exportExcel: (body: PlanExportBody) =>
    apiClient
      .post<Blob>('/v1/plan-produccion/export', body, { responseType: 'blob', timeout: 60_000 })
      .then((r) => r.data),

  /** Persistencia en Dataverse (av_planproduccion). 501 si la entidad no existe aún. */
  savePlan: (body: { empresa: string; anio: number; nombre: string; plan: PlanExportBody['plan'] }) =>
    apiClient.post('/v1/plan-produccion/save', body).then((r) => r.data),

  /** Líneas de compra pendientes de una semana (sidebar de semana). */
  getEntradasDetalle: (params: { empresa: string; anio: number; semana: number }) =>
    apiClient
      .get<ApiResponse<PlanEntradaDetalle[]>>('/v1/plan-produccion/entradas-detalle', { params })
      .then((r) => r.data),

  /** Escenarios guardados (cabeceras). 501 si la entidad no existe aún. */
  listScenarios: (params: { empresa: string; anio?: number }) =>
    apiClient
      .get<ApiResponse<PlanScenario[]>>('/v1/plan-produccion/scenarios', { params })
      .then((r) => r.data),

  /** Escenario completo (cabecera + líneas) por id. */
  getScenario: (id: string) =>
    apiClient
      .get<ApiResponse<PlanScenarioDetail>>(`/v1/plan-produccion/scenarios/${id}`)
      .then((r) => r.data),
}

// ─── Dashboard ejecutivo ──────────────────────────────────────────────────────

export const dashboardApi = {
  getResumen: () =>
    apiClient
      .get<ApiResponse<DashboardResumen>>('/v1/dashboard/resumen')
      .then((r) => r.data),

  getProduccionesRiesgo: () =>
    apiClient
      .get<ApiResponse<ProduccionRiesgoItem[]>>('/v1/dashboard/producciones-riesgo')
      .then((r) => r.data),

  getProduccionSemanal: () =>
    apiClient
      .get<ApiResponse<ProduccionSemanalPoint[]>>('/v1/dashboard/produccion-semanal')
      .then((r) => r.data),
}

// ─── Secuencia de Producción ──────────────────────────────────────────────────

export const secuenciaApi = {
  getSecuencia: (params?: { desde?: string; hasta?: string }) =>
    apiClient
      .get<ApiResponse<SecuenciaProduccionRow[]>>('/v1/secuencia-produccion', { params })
      .then((r) => r.data),

  getComponentes: () =>
    apiClient
      .get<ApiResponse<ComponenteSecuenciaRow[]>>('/v1/secuencia-produccion/componentes')
      .then((r) => r.data),

  /** Solo el overlay de Dataverse (motivo de desviación/calidad) — sondeo ligero. */
  getOverlay: () =>
    apiClient
      .get<ApiResponse<SecuenciaOverlayRow[]>>('/v1/secuencia-produccion/overlay')
      .then((r) => r.data),

  upsertOverlay: (produccion: string, body: OverlayBody) =>
    apiClient
      .post<ApiResponse<OverlayResult>>(
        `/v1/secuencia-produccion/${encodeURIComponent(produccion)}/overlay`,
        body,
      )
      .then((r) => r.data),
}

// ─── Ficha Técnica (BOM) — backend Dataverse directo ──────────────────────────
// Con VITE_BOM_USE_MOCKS=true toda la UI hace roundtrip contra un store en memoria
// (carga diferida, no entra en el bundle de producción).
const BOM_USE_MOCKS = import.meta.env.VITE_BOM_USE_MOCKS === 'true'
const bomMock = () => import('@/mocks/bomStore').then((m) => m.bomMockStore)

const enc = encodeURIComponent
// base de una versión concreta: /v1/bom/{item}/{empresa}/{formula}
const vBase = (itemId: string, empresa: string, formula: string) =>
  `/v1/bom/${enc(itemId)}/${enc(empresa)}/${enc(formula)}`

export interface UploadImagenArgs {
  productnumber: string
  esProductoFinal: boolean
  file: File
}

export const bomCapturaApi = {
  getSistemas: (q = '') =>
    BOM_USE_MOCKS
      ? bomMock().then((s) => s.getSistemas(q)).then((data) => ({ data, updatedAt: new Date().toISOString() }))
      : apiClient.get<ApiResponse<SistemaBOM[]>>('/v1/bom/sistemas', { params: { q } }).then((r) => r.data),

  getVersiones: (itemId: string) =>
    BOM_USE_MOCKS
      ? bomMock().then((s) => s.getVersiones(itemId)).then((data) => ({ data, updatedAt: new Date().toISOString() }))
      : apiClient.get<ApiResponse<VersionBOM[]>>(`/v1/bom/${enc(itemId)}/versiones`).then((r) => r.data),

  getCatalogoCaracteristicas: (): Promise<ApiResponse<VarianteCaracteristicas[]>> =>
    BOM_USE_MOCKS
      ? Promise.resolve({ data: [], updatedAt: new Date().toISOString() })
      : apiClient.get<ApiResponse<VarianteCaracteristicas[]>>('/v1/bom/catalogo-caracteristicas').then((r) => r.data),

  getBOM: (itemId: string, empresa: string, formula: string) =>
    BOM_USE_MOCKS
      ? bomMock().then((s) => s.getBOM(itemId, empresa, formula)).then((data) => ({ data, updatedAt: new Date().toISOString() }))
      : apiClient.get<ApiResponse<BOMCompleto>>(vBase(itemId, empresa, formula)).then((r) => r.data),

  getImagen: (itemId: string, empresa: string, formula: string, productnumber: string): Promise<string | null> =>
    BOM_USE_MOCKS
      ? bomMock().then((s) => s.getImagen(itemId, productnumber))
      : apiClient
          .get<ApiResponse<ImagenResponse>>(`${vBase(itemId, empresa, formula)}/imagen/${enc(productnumber)}`)
          .then((r) => r.data.data.dataUri),

  getCapturaImagen: (itemId: string, empresa: string, formula: string, capturaId: string): Promise<string | null> =>
    BOM_USE_MOCKS
      ? bomMock().then((s) => s.getCapturaImagen(itemId, capturaId))
      : apiClient
          .get<ApiResponse<ImagenResponse>>(`${vBase(itemId, empresa, formula)}/capturas/${enc(capturaId)}/imagen`)
          .then((r) => r.data.data.dataUri),

  addCaptura: (itemId: string, empresa: string, formula: string, body: CapturaCreateRequest): Promise<CapturaBOM> =>
    BOM_USE_MOCKS
      ? bomMock().then((s) => s.addCaptura(itemId, body))
      : apiClient.post<ApiResponse<CapturaBOM>>(`${vBase(itemId, empresa, formula)}/capturas`, body).then((r) => r.data.data),

  updateCaptura: (itemId: string, empresa: string, formula: string, capturaId: string, body: CapturaUpdateRequest): Promise<void> =>
    BOM_USE_MOCKS
      ? bomMock().then((s) => s.updateCaptura(itemId, capturaId, body))
      : apiClient.patch(`${vBase(itemId, empresa, formula)}/capturas/${enc(capturaId)}`, body).then(() => undefined),

  deleteCaptura: (itemId: string, empresa: string, formula: string, capturaId: string): Promise<void> =>
    BOM_USE_MOCKS
      ? bomMock().then((s) => s.deleteCaptura(itemId, capturaId))
      : apiClient.delete(`${vBase(itemId, empresa, formula)}/capturas/${enc(capturaId)}`).then(() => undefined),

  setLineaPosicion: (itemId: string, empresa: string, formula: string, lineaId: string, position: number): Promise<void> =>
    BOM_USE_MOCKS
      ? bomMock().then((s) => s.setLineaPosicion(itemId, lineaId, position))
      : apiClient.patch(`${vBase(itemId, empresa, formula)}/lineas/${enc(lineaId)}/posicion`, { position }).then(() => undefined),

  generarFicha: (itemId: string, empresa: string, formula: string): Promise<GenerarFichaResponse> =>
    BOM_USE_MOCKS
      ? Promise.resolve({ jobId: `job-${Date.now().toString(16)}`, estado: 'encolado' })
      : apiClient.post<ApiResponse<GenerarFichaResponse>>(`${vBase(itemId, empresa, formula)}/generar-ficha`, {}).then((r) => r.data.data),

  /** Sube/reemplaza una imagen de producto (componente o producto final) en mserp. */
  uploadImagen: (
    itemId: string, empresa: string, formula: string,
    args: UploadImagenArgs,
    onProgress?: (pct: number) => void,
  ): Promise<ImagenUploadResponse> => {
    if (BOM_USE_MOCKS) {
      onProgress?.(100)
      return bomMock().then((s) => s.uploadImagen(itemId, args.productnumber, args.esProductoFinal, args.file))
    }
    const form = new FormData()
    form.append('imagen', args.file)
    form.append('productnumber', args.productnumber)
    form.append('es_producto_final', String(args.esProductoFinal))
    return apiClient
      .post<ApiResponse<ImagenUploadResponse>>(`${vBase(itemId, empresa, formula)}/imagen`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60_000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
        },
      })
      .then((r) => r.data.data)
  },
}

// ─── Registro de actividad (auditoría) ────────────────────────────────────────
// Fire-and-forget: la auditoría nunca debe romper la UX, por eso se traga errores.
export interface AuditEvent {
  accion: string
  ruta?: string
  detalle?: string
  resultado?: string
  sesion_id?: string
}

/** ID de sesión de navegador (única fuente; agrupa todos los eventos de una sesión). */
export function getAuditSessionId(): string {
  const KEY = 'airval_audit_sid'
  let sid = sessionStorage.getItem(KEY)
  if (!sid) {
    sid = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
    sessionStorage.setItem(KEY, sid)
  }
  return sid
}

export const auditApi = {
  // Adjunta el sesion_id automáticamente (salvo que el llamante pase uno), para que
  // TODOS los eventos (login, page_view, comentarios…) lo lleven informado.
  log: (event: AuditEvent) =>
    apiClient
      .post('/v1/audit', { ...event, sesion_id: event.sesion_id ?? getAuditSessionId() })
      .then((r) => r.data)
      .catch(() => undefined),
}

// ─── Cargas de salida (Outbound loads) ───────────────────────────────

export const cargasSalidaApi = {
  /** Las cargas de salida vivas, con su picking ya agregado.
   *
   *  No se pagina ni se acota por fecha desde aquí: la pantalla agrupa por
   *  proximidad (atrasadas / hoy / esta semana / …) y para eso necesita el
   *  conjunto entero. El backend ya deja fuera las expedidas, las cargas
   *  abandonadas sin fecha ni picking, y todo lo que caiga a más de 30 días. */
  list: (params?: { incluirExpedidas?: boolean; almacen?: string }) =>
    apiClient
      .get<ApiResponse<CargaSalida[]>>('/v1/cargas-salida', { params })
      .then((r) => r.data),

  /** Diccionario código -> descripción de los artículos de las cargas (~1.100
   *  filas, ~42 KB: va entero y de una vez). */
  getArticulos: () =>
    apiClient
      .get<ApiResponse<ArticuloCatalogo[]>>('/v1/cargas-salida/articulos')
      .then((r) => r.data),

  /** El detalle de una carga. 404 tanto si no existe como si no es del alcance
   *  del usuario: el backend no distingue una cosa de la otra a propósito. */
  getLineas: (carga: string) =>
    apiClient
      .get<ApiResponse<CargaSalidaLinea[]>>(
        `/v1/cargas-salida/${encodeURIComponent(carga)}/lineas`,
      )
      .then((r) => r.data),

  /** Las líneas de varias cargas de una vez, para la hoja «Líneas» del Excel.
   *  Va por POST porque la lista la pone la pantalla y 545 números de carga en
   *  una query string son ~7 KB de URL. Puede responder 413 si se pasa del
   *  techo del servidor. */
  getLineasBulk: (cargas: string[]) =>
    apiClient
      .post<ApiResponse<CargaSalidaLinea[]>>('/v1/cargas-salida/lineas-bulk', { cargas })
      .then((r) => r.data),

  /** Decisión sobre una línea (parcial / esperar / eliminar). `null` la quita.
   *  Devuelve la línea ya actualizada. Rechazos con `detail: {codigo, valores}`:
   *  `linea_ya_preparada`, `back_order_firme`, `dataverse_no_disponible`… */
  setAccionLinea: (carga: string, transid: string, accion: AccionLinea | null) =>
    apiClient
      .put<ApiResponse<CargaSalidaLinea>>(
        `/v1/cargas-salida/${encodeURIComponent(carga)}/lineas/${encodeURIComponent(transid)}/accion`,
        { accion },
      )
      .then((r) => r.data),

  /** Marca manual (transporte / transporte confirmado) o `estado: null` para quitarla. */
  setEstadoManual: (carga: string, body: EstadoManualBody) =>
    apiClient
      .put<ApiResponse<EstadoManualResultado>>(
        `/v1/cargas-salida/${encodeURIComponent(carga)}/estado-manual`,
        body,
      )
      .then((r) => r.data),

  /** Manda el aviso al cliente con la documentación. `mock: true` = flujo sin conectar. */
  avisarCliente: (carga: string) =>
    apiClient
      .post<ApiResponse<AvisoClienteResultado>>(
        `/v1/cargas-salida/${encodeURIComponent(carga)}/aviso-cliente`,
      )
      .then((r) => r.data),

  getBackOrders: () =>
    apiClient
      .get<ApiResponse<BackOrder[]>>('/v1/cargas-salida/back-orders')
      .then((r) => r.data),

  cancelarBackOrder: (id: string, motivo: string) =>
    apiClient
      .post<ApiResponse<BackOrder>>(
        `/v1/cargas-salida/back-orders/${encodeURIComponent(id)}/cancelar`,
        { motivo },
      )
      .then((r) => r.data),

  /** Facturas de los pedidos de la carga. Vacío = todavía sin facturar. */
  getFacturas: (carga: string) =>
    apiClient
      .get<ApiResponse<FacturaCarga[]>>(`/v1/cargas-salida/${encodeURIComponent(carga)}/facturas`)
      .then((r) => r.data),

  getFacturaAdjuntos: (carga: string, recid: string) =>
    apiClient
      .get<ApiResponse<AdjuntoDoc[]>>(
        `/v1/cargas-salida/${encodeURIComponent(carga)}/facturas/${encodeURIComponent(recid)}/adjuntos`,
      )
      .then((r) => r.data),
}

// ─── Chat de carga ──────────────────────────────────────────────────

export const comentariosApi = {
  /** El hilo de la carga, el MÁS RECIENTE PRIMERO (orden del backend). */
  list: (carga: string) =>
    apiClient
      .get<ApiResponse<ComentarioCarga[]>>(
        `/v1/cargas-salida/${encodeURIComponent(carga)}/comentarios`,
      )
      .then((r) => r.data),

  /** Sólo el texto: autor y empresa los pone el servidor desde el token. */
  create: (carga: string, body: ComentarioCreateRequest) =>
    apiClient
      .post<ApiResponse<ComentarioCarga>>(
        `/v1/cargas-salida/${encodeURIComponent(carga)}/comentarios`,
        body,
      )
      .then((r) => r.data),
}

// ─── Cuadro de muelles (Dock schedule) ──────────────────────────────

export const muellesApi = {
  /** El catálogo de slots del almacén. */
  listSlots: (almacen?: string) =>
    apiClient
      .get<ApiResponse<SlotMuelle[]>>('/v1/muelles', { params: { almacen } })
      .then((r) => r.data),

  /** El cuadro de una ventana de tiempo: slots, camiones y sus cargas.
   *
   *  `desde` y `hasta` van en ISO **con zona**. El backend rechaza una fecha sin
   *  offset, porque interpretarla como UTC movería el camión dos horas. */
  getCuadro: (params: { desde: string; hasta: string; almacen?: string }) =>
    apiClient
      .get<ApiResponse<CuadroMuelles>>('/v1/muelles/cuadro', { params })
      .then((r) => r.data),

  crearCamion: (body: CamionCreateRequest) =>
    apiClient
      .post<ApiResponse<CamionMuelle>>('/v1/muelles/camiones', body)
      .then((r) => r.data),

  /** `camionId` es el GUID, no el código autonumérico: es lo que no cambia. */
  actualizarCamion: (camionId: string, body: CamionUpdateRequest) =>
    apiClient
      .patch<ApiResponse<CamionMuelle>>(
        `/v1/muelles/camiones/${encodeURIComponent(camionId)}`,
        body,
      )
      .then((r) => r.data),

  /** Borra el camión y, con él, sus asignaciones de carga. Devuelve cuántas
   *  cayeron: las dos tablas se relacionan por texto y no por lookup, así que la
   *  cascada la hace el backend a mano y conviene poder enseñarla. */
  borrarCamion: (camionId: string) =>
    apiClient
      .delete<ApiResponse<{ asignacionesBorradas: number }>>(
        `/v1/muelles/camiones/${encodeURIComponent(camionId)}`,
      )
      .then((r) => r.data),

  asignarCarga: (camionId: string, carga: string) =>
    apiClient
      .post<ApiResponse<AsignacionCarga>>(
        `/v1/muelles/camiones/${encodeURIComponent(camionId)}/cargas`,
        { carga },
      )
      .then((r) => r.data),

  /** Da de alta un muelle en el catálogo. */
  crearSlot: (body: SlotCreateRequest) =>
    apiClient
      .post<ApiResponse<SlotMuelle>>('/v1/muelles/slots', body)
      .then((r) => r.data),

  /** Edita un muelle. Si cambia el nombre, el backend **arrastra a sus
   *  camiones** y devuelve cuántos: el camión guarda el nombre como texto, así
   *  que renombrar sin arrastrar los dejaría apuntando a la nada. */
  actualizarSlot: (slotId: string, body: SlotUpdateRequest) =>
    apiClient
      .patch<ApiResponse<{ slot: SlotMuelle; camionesArrastrados: number }>>(
        `/v1/muelles/slots/${encodeURIComponent(slotId)}`,
        body,
      )
      .then((r) => r.data),

  /** Borra un muelle. Se rechaza con 409 si tiene camiones, salvo `forzar`. */
  borrarSlot: (slotId: string, forzar = false) =>
    apiClient
      .delete<ApiResponse<{ camionesHuerfanos: number }>>(
        `/v1/muelles/slots/${encodeURIComponent(slotId)}`,
        { params: { forzar } },
      )
      .then((r) => r.data),

  quitarCarga: (camionId: string, carga: string) =>
    apiClient
      .delete<ApiResponse<{ borrada: boolean }>>(
        `/v1/muelles/camiones/${encodeURIComponent(camionId)}/cargas/${encodeURIComponent(carga)}`,
      )
      .then((r) => r.data),
}

// ─── Alta de pedido de venta desde archivo ────────────────────────────────────
//
// Dos llamadas con tiempos muy distintos: la lectura el usuario la ESPERA (de ahí
// el timeout largo y el progreso de subida), y la creación responde 202 al
// instante porque el pedido lo crea un flujo que tarda minutos.
export const pedidosArchivoApi = {
  /** Los almacenes desde los que se puede expedir. */
  almacenes: () =>
    apiClient
      .get<ApiResponse<AlmacenVenta[]>>('/v1/ordenes-venta/almacenes')
      .then((r) => r.data.data),

  /** Manda el archivo al flujo que lo lee. El timeout acompaña al del backend
   *  (120 s) con margen: cortar aquí antes dejaría al usuario sin saber nada
   *  mientras el flujo sigue trabajando. */
  leer: (
    args: { archivos: File[]; empresa: string; grupo: GrupoCliente },
    onProgress?: (pct: number) => void,
  ): Promise<LecturaArchivo> => {
    const form = new FormData()
    // El mismo nombre repetido: así lo recoge FastAPI como `list[UploadFile]`.
    // Hacia el navegador seguimos en multipart —nativo, sin inflar nada y con
    // barra de progreso—; es el backend quien lo pasa a base64 para el flujo.
    for (const archivo of args.archivos) form.append('archivos', archivo)
    form.append('empresa', args.empresa)
    form.append('grupo', args.grupo)
    return apiClient
      .post<ApiResponse<LecturaArchivo>>('/v1/ordenes-venta/desde-archivo/lectura', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 150_000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
        },
      })
      .then((r) => r.data.data)
  },

  /** Cursa la solicitud. Responde 202: el pedido todavía no existe. */
  crear: (body: PedidoArchivoRequest) =>
    apiClient
      .post<ApiResponse<PedidoArchivoResult>>('/v1/ordenes-venta/desde-archivo', body)
      .then((r) => r.data.data),

  /** Las solicitudes recientes de quien pregunta. */
  solicitudes: (empresa?: string) =>
    apiClient
      .get<ApiResponse<SolicitudPedidoArchivo[]>>('/v1/ordenes-venta/desde-archivo/solicitudes', {
        params: empresa ? { empresa } : undefined,
      })
      .then((r) => r.data.data),
}

/** Situación de ventas (bloque 4). SÓLO en local: en Azure la ruta no existe. */
export const ventasSituacionApi = {
  get: (periodo: string) =>
    apiClient
      .get<ApiResponse<VentasSituacion>>('/v1/ventas-situacion', { params: { periodo } })
      .then((r) => r.data),

  facturacion: (empresa: string, pedido: string, carga: string) =>
    apiClient
      .get<ApiResponse<FacturacionReferencia[]>>(
        `/v1/ventas-situacion/${encodeURIComponent(empresa)}/${encodeURIComponent(pedido)}/facturacion`,
        { params: { carga } },
      )
      .then((r) => r.data),
}
